import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, ChevronLeft, Users, PenSquare, Search } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("access_token")}` });

// ─────────────────────────────────────────────
// Contact Picker — shows who you can message
// ─────────────────────────────────────────────
const ContactPicker = ({ userRole, userId, onSelect, onBack }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (userRole === "tutor") {
          const res = await axios.get(`${API}/tutor/my-students`, { headers: authHeaders() });
          // Deduplicate by student_id
          const seen = new Set();
          const unique = res.data.filter(s => {
            if (seen.has(s.student_id)) return false;
            seen.add(s.student_id);
            return true;
          });
          setContacts(unique.map(s => ({ id: s.student_id, name: s.student_name, role: "student", subtitle: s.course_name })));
        } else if (userRole === "student") {
          const res = await axios.get(`${API}/enrollments/my`, { headers: authHeaders() });
          const seen = new Set();
          const unique = res.data.filter(e => {
            if (!e.tutor_id || seen.has(e.tutor_id)) return false;
            seen.add(e.tutor_id);
            return true;
          });
          setContacts(unique.map(e => ({ id: e.tutor_id, name: e.tutor_name || "Your Tutor", role: "tutor", subtitle: e.course_name })));
        } else {
          setContacts([]); // admins see all threads, don't need to "start" one
        }
      } catch {
        toast.error("Couldn't load contacts");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userRole]);

  const startConversation = async (contact) => {
    setStarting(contact.id);
    try {
      const res = await axios.post(`${API}/messages/conversations`, {
        recipient_id: contact.id,
        recipient_role: contact.role
      }, { headers: authHeaders() });
      onSelect({ id: res.data.id, participant_names: { [userId]: "You", [contact.id]: contact.name }, participant_roles: { [userId]: userRole, [contact.id]: contact.role } });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start conversation");
    } finally {
      setStarting(null);
    }
  };

  const filtered = contacts.filter(c => c.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 p-3 border-b-2 border-[#0A0A0A] bg-[#F0EFEB]">
        <button onClick={onBack} className="p-1 hover:bg-[#D6E6FA] rounded" data-testid="contacts-back-btn">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="font-bold text-sm">New Message</span>
      </div>
      <div className="p-2 border-b border-[#0A0A0A]/10">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#4A4A4A]" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search contacts..."
            className="w-full pl-7 pr-3 py-1.5 text-xs border border-[#0A0A0A]/20 rounded-lg bg-white focus:outline-none focus:border-[#3C8DE4]"
            data-testid="contact-search-input"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <p className="text-xs text-center text-[#4A4A4A] py-6">Loading contacts...</p>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto text-[#4A4A4A] mb-2" />
            <p className="text-xs text-[#4A4A4A]">
              {contacts.length === 0
                ? userRole === "admin"
                  ? "Use the Admin Messages page to view all threads."
                  : "No contacts yet. Book a session first to unlock messaging."
                : "No results found."}
            </p>
          </div>
        ) : (
          filtered.map(c => (
            <button
              key={c.id}
              onClick={() => startConversation(c)}
              disabled={starting === c.id}
              className="w-full flex items-center gap-3 p-3 hover:bg-[#F0EFEB] border-b border-[#0A0A0A]/5 text-left transition-colors"
              data-testid={`contact-${c.id}`}
            >
              <div className="w-9 h-9 rounded-full bg-[#3C8DE4] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                {c.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{c.name}</div>
                <div className="text-xs text-[#4A4A4A] capitalize truncate">{c.role} · {c.subtitle}</div>
              </div>
              {starting === c.id && <div className="w-4 h-4 border-2 border-[#3C8DE4] border-t-transparent rounded-full animate-spin" />}
            </button>
          ))
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Conversation Thread
// ─────────────────────────────────────────────
const ConversationThread = ({ conv, userId, onBack, onNewMessage }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations/${conv.id}`, { headers: authHeaders() });
      setMessages(res.data.messages);
      onNewMessage();
    } catch {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [conv.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/conversations/${conv.id}/send`, { content: text }, { headers: authHeaders() });
      setInput("");
      load();
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const otherName = Object.entries(conv.participant_names || {})
    .filter(([id]) => id !== userId)
    .map(([, name]) => name)
    .join(", ") || "Conversation";

  const otherRole = Object.entries(conv.participant_roles || {})
    .filter(([id]) => id !== userId)
    .map(([, role]) => role)
    .join(", ");

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 p-3 border-b-2 border-[#0A0A0A] bg-[#F0EFEB]">
        <button onClick={onBack} className="p-1 hover:bg-[#D6E6FA] rounded" data-testid="thread-back-btn">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="w-8 h-8 rounded-full bg-[#3C8DE4] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
          {otherName.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm truncate">{otherName}</div>
          <div className="text-xs text-[#4A4A4A] capitalize">{otherRole}</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#FDFBF7]">
        {loading ? (
          <p className="text-xs text-center text-[#4A4A4A] py-4">Loading...</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-center text-[#4A4A4A] py-4 italic">
            Say hi to {otherName}!
          </p>
        ) : (
          messages.map(m => {
            const isMine = m.sender_id === userId;
            return (
              <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                  isMine
                    ? "bg-[#3C8DE4] text-white rounded-tr-sm"
                    : "bg-white border-2 border-[#0A0A0A]/10 text-[#0A0A0A] rounded-tl-sm"
                }`} data-testid={`msg-${m.id}`}>
                  {!isMine && <div className="text-xs font-bold mb-0.5 opacity-70">{m.sender_name}</div>}
                  <p className="break-words">{m.content}</p>
                  <div className={`text-xs mt-1 opacity-60 ${isMine ? "text-right" : ""}`}>{timeAgo(m.created_at)}</div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <div className="p-3 border-t-2 border-[#0A0A0A]/10 bg-white">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
            placeholder="Type a message..."
            className="flex-1 brutalist-input text-sm py-2"
            data-testid="message-input"
            disabled={sending}
          />
          <button
            onClick={send}
            disabled={sending || !input.trim()}
            className="brutalist-btn-primary px-3 py-2 flex items-center gap-1 text-sm"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Conversation List
// ─────────────────────────────────────────────
const ConversationList = ({ conversations, userId, onSelect, onCompose, loading }) => (
  <div className="flex flex-col h-full">
    <div className="flex items-center justify-between p-3 border-b-2 border-[#0A0A0A] bg-[#F0EFEB]">
      <h3 className="font-bold text-sm">Messages</h3>
      <button
        onClick={onCompose}
        className="p-1.5 hover:bg-[#D6E6FA] rounded-lg transition-colors"
        title="New message"
        data-testid="compose-message-btn"
      >
        <PenSquare className="w-4 h-4" />
      </button>
    </div>
    <div className="flex-1 overflow-y-auto">
      {loading ? (
        <p className="text-xs text-center text-[#4A4A4A] py-6">Loading...</p>
      ) : conversations.length === 0 ? (
        <div className="p-4 text-center">
          <MessageCircle className="w-8 h-8 mx-auto text-[#4A4A4A] mb-2" />
          <p className="text-xs text-[#4A4A4A] mb-2">No conversations yet.</p>
          <button
            onClick={onCompose}
            className="text-xs text-[#3C8DE4] font-bold underline"
            data-testid="start-first-conversation-btn"
          >
            Start one →
          </button>
        </div>
      ) : (
        conversations.map(conv => {
          const otherName = Object.entries(conv.participant_names || {})
            .filter(([id]) => id !== userId)
            .map(([, name]) => name)
            .join(", ") || "Conversation";
          const otherRole = Object.entries(conv.participant_roles || {})
            .filter(([id]) => id !== userId)
            .map(([, role]) => role)
            .join(", ");
          return (
            <button
              key={conv.id}
              onClick={() => onSelect(conv)}
              className="w-full flex items-center gap-3 p-3 hover:bg-[#F0EFEB] border-b border-[#0A0A0A]/5 text-left transition-colors"
              data-testid={`conversation-${conv.id}`}
            >
              <div className="relative flex-shrink-0">
                <div className="w-9 h-9 rounded-full bg-[#3C8DE4] text-white flex items-center justify-center font-bold text-sm">
                  {otherName.charAt(0)}
                </div>
                {conv.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#FF6B6B] text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {conv.unread_count > 9 ? "9+" : conv.unread_count}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-1">
                  <span className="font-bold text-sm truncate">{otherName}</span>
                  <span className="text-xs text-[#4A4A4A] flex-shrink-0">{timeAgo(conv.last_message_at)}</span>
                </div>
                <div className="text-xs text-[#4A4A4A] capitalize mb-0.5">{otherRole}</div>
                <p className="text-xs text-[#4A4A4A] truncate">{conv.last_message_preview || "Start a conversation"}</p>
              </div>
            </button>
          );
        })
      )}
    </div>
  </div>
);

// ─────────────────────────────────────────────
// Main Sidebar Component
// ─────────────────────────────────────────────
const MessageSidebar = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // "list" | "contacts" | "thread"
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadConversations = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/messages/conversations`, { headers: authHeaders() });
      setConversations(res.data);
      setTotalUnread(res.data.reduce((s, c) => s + (c.unread_count || 0), 0));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setLoading(true);
      loadConversations();
      const interval = setInterval(loadConversations, 30000);
      return () => clearInterval(interval);
    }
  }, [user, loadConversations]);

  // When sidebar closes, reset to list view
  useEffect(() => { if (!open) setView("list"); }, [open]);

  // Admins use the dedicated Messages page (with per-student/tutor entry points)
  // instead of the floating widget.
  if (!user || user.role === "admin") return null;

  const handleSelectConv = (conv) => { setActiveConv(conv); setView("thread"); };
  const handleBack = () => { setView("list"); setActiveConv(null); loadConversations(); };
  const handleComposeSelect = (conv) => { setActiveConv(conv); setView("thread"); };

  return (
    <>
      {/* Floating toggle button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#0A0A0A] text-white shadow-xl flex items-center justify-center hover:bg-[#3C8DE4] transition-colors border-2 border-[#0A0A0A]"
        data-testid="message-sidebar-toggle"
        aria-label="Open messages"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!open && totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF6B6B] text-white text-xs rounded-full flex items-center justify-center font-bold pointer-events-none">
            {totalUnread > 9 ? "9+" : totalUnread}
          </span>
        )}
      </button>

      {/* Sidebar panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-80 h-[500px] bg-white border-2 border-[#0A0A0A] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="message-sidebar-panel"
        >
          {view === "thread" && activeConv ? (
            <ConversationThread
              conv={activeConv}
              userId={user.id}
              onBack={handleBack}
              onNewMessage={loadConversations}
            />
          ) : view === "contacts" ? (
            <ContactPicker
              userRole={user.role}
              userId={user.id}
              onSelect={handleComposeSelect}
              onBack={() => setView("list")}
            />
          ) : (
            <ConversationList
              conversations={conversations}
              userId={user.id}
              onSelect={handleSelectConv}
              onCompose={() => setView("contacts")}
              loading={loading}
            />
          )}
        </div>
      )}
    </>
  );
};

export default MessageSidebar;
